import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as FileSaver from 'file-saver';
import { debounce } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  CheckboxComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  WizardStep
} from 'mbs-ui-kit';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import moment from 'moment';
import { catchError, finalize, noop, of } from 'rxjs';

import { I18NextPipe } from 'angular-i18next';

import { CommonModule } from '@angular/common';

const COPY_ANIMATION_DURATION = 1500;

@UntilDestroy()
@Component({
  selector: 'mbs-enable-password-recovery-wizard-component',
  templateUrl: 'enable-password-recovery-wizard.component.html',
  styleUrls: ['./enable-password-recovery-wizard.component.scss'],
  imports: [
    AlertComponent,
    ButtonComponent,
    CheckboxComponent,
    CommonModule,
    I18NextPipe,
    ReactiveFormsModule,
    ModalComponent,
    ModalFooterDirective
  ]
})
export class EnablePasswordRecoveryWizardComponent implements OnInit {
  private providerService = inject(ProviderService);
  private clipboardService = inject(Clipboard);
  private TFAService = inject(TFAService);

  public readonly alertType = MbsPopupType;

  public publicKey: string;
  public pemKey: string;
  public privateKey: string;
  public isOverlayVisible = false;
  public isCopyButtonClicked = false;
  public confirmControl = new FormControl(false);
  public currentStep: WizardStep;
  public closable = false;
  public loading = false;
  public recoveryStateLoading = false;
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  //
  async ngOnInit() {
    this.loading = true;
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 4096,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-1'
      },
      true,
      ['encrypt', 'decrypt']
    );
    const buffer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);
    this.privateKey = window.btoa(this.arrayBufferToString(buffer));
    this.publicKey = JSON.stringify(await crypto.subtle.exportKey('jwk', keyPair.publicKey));

    this.loading = false;
  }

  public handleCopyOnButton(): void {
    this.debouncedCopyPkToClipboard();
    this.isCopyButtonClicked = true;
    setTimeout(() => {
      this.isCopyButtonClicked = false;
    }, COPY_ANIMATION_DURATION);
  }

  public handleCopyOnText(): void {
    this.debouncedCopyPkToClipboard();
    this.isOverlayVisible = true;
    setTimeout(() => {
      this.isOverlayVisible = false;
    }, COPY_ANIMATION_DURATION);
  }

  private handleCopyPrivateKey = (): void => {
    const pem = this.formatAsPem(this.privateKey);
    this.clipboardService.copy(pem);
  };

  public debouncedCopyPkToClipboard = debounce(this.handleCopyPrivateKey, COPY_ANIMATION_DURATION) as () => void;

  handleDownloadPrivateKey(): void {
    const pem = this.formatAsPem(this.privateKey);
    const file = new Blob([pem], { type: 'text/plain' });
    FileSaver.saveAs(file, `password-recovery-key-${moment().format('MMM-DD-YYYY_hh-mm-a')}.txt`.toLowerCase());
  }

  handleEnable(): void {
    this.recoveryStateLoading = true;
    this.providerService
      .setPasswordRecoveryState(true, this.publicKey ? JSON.parse(this.publicKey) : false)
      .pipe(
        catchError((error) => of(error)),
        finalize(() => (this.recoveryStateLoading = false))
      )
      .subscribe((result) => {
        if (result?.error?.authId) {
          this.showTFAModal(result.error);
        } else {
          this.baseModal.save();
        }
      });
  }

  showTFAModal(data) {
    this.TFAService.openApproveModal({ title: 'Enable Password Recovery', ...data })
      .then(() => {
        this.handleEnable();
      })
      .catch(noop);
  }

  handleClose(): void {
    this.baseModal.save();
  }

  arrayBufferToString(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return binary;
  }

  formatAsPem(str): string {
    let finalString = '-----BEGIN PRIVATE KEY-----\n';

    while (str.length > 0) {
      finalString += str.substring(0, 64) + '\n';
      str = str.substring(64);
    }

    finalString = finalString + '-----END PRIVATE KEY-----';

    return finalString;
  }
}
